<template>
	<div>
		<!-- Page Header -->
		<JuniperPageHeader back="/onboarding" title="Onboarding Settings" subtitle="Types, steps and fields" />

		<div class="os-layout">
			<!-- Types — master list -->
			<aside class="os-panel os-types-panel">
				<div class="os-panel-head">
					<div class="os-section-title">Types</div>
					<button class="os-btn os-btn--sm" data-bs-toggle="modal" data-bs-target="#typeModal">
						<i class="fas fa-plus"></i> Add
					</button>
				</div>
				<div class="os-type-list">
					<button
						v-for="type in onboardingTypes"
						:key="type.id"
						class="os-type-item"
						:class="{ active: selectedTypeId === type.id }"
						@click="selectType(type.id)"
					>
						<span class="os-type-name">{{ type.name }}</span>
						<span class="os-type-count">{{ type.steps.length }}</span>
					</button>
					<div v-if="onboardingTypes.length === 0" class="os-muted os-muted--pad">No onboarding types yet.</div>
				</div>
			</aside>

			<!-- Selected type — steps & fields -->
			<section class="os-panel os-detail-panel">
				<div v-if="!selectedType" class="os-empty">
					<i class="fad fa-layer-group os-empty-icon"></i>
					<p>Select an onboarding type to manage its steps and fields.</p>
				</div>

				<template v-else>
					<div class="os-detail-head">
						<div class="os-detail-titles">
							<h3 class="os-detail-title">{{ selectedType.name }}</h3>
							<p v-if="selectedType.description" class="os-detail-desc">{{ selectedType.description }}</p>
						</div>
						<div class="os-detail-actions">
							<button class="os-btn os-btn--sm" data-bs-toggle="modal" data-bs-target="#stepModal" @click="setStepOnboardingTypeId(selectedType.id, selectedType.steps)">
								<i class="fas fa-plus"></i> Add Step
							</button>
							<button class="os-btn os-btn--sm os-btn--danger" @click="askRemoveType(selectedType)">
								<i class="fas fa-trash"></i> Delete Type
							</button>
						</div>
					</div>

					<!-- Steps -->
					<draggable v-model="selectedType.steps" handle=".os-drag" @change="updateStepsOrder(selectedType.steps)">
						<div class="os-step" v-for="step in selectedType.steps" :key="step.id">
							<div class="os-step-head" @click="toggleStep(step.id)">
								<span class="os-drag" title="Drag to reorder" @click.stop>
									<i class="fas fa-grip-vertical"></i>
								</span>
								<i class="fas fa-chevron-right os-step-chevron" :class="{ 'os-step-chevron--open': expandStep.includes(step.id) }"></i>
								<span class="os-step-name">{{ step.name }}</span>
								<span class="os-step-count">{{ step.fields.length }} {{ step.fields.length === 1 ? 'field' : 'fields' }}</span>
								<span class="os-row-actions" @click.stop>
									<button class="os-icon-btn" title="Edit step" data-bs-toggle="modal" data-bs-target="#stepModal" @click="editOnboardingStep(step)">
										<i class="fas fa-pen"></i>
									</button>
									<button class="os-icon-btn os-icon-btn--danger" title="Delete step" @click="askRemoveStep(step)">
										<i class="fas fa-trash"></i>
									</button>
								</span>
							</div>

							<div v-if="expandStep.includes(step.id)" class="os-step-body">
								<draggable v-if="step.fields.length > 0" v-model="step.fields" handle=".os-drag" @change="updateFieldsOrder(step.fields)">
									<div class="os-field" v-for="field in step.fields" :key="field.id">
										<span class="os-drag" title="Drag to reorder">
											<i class="fas fa-grip-vertical"></i>
										</span>
										<span class="os-field-name">{{ field.name }}</span>
										<code class="os-field-code">{{ field.type }} · field_{{ field.id }}</code>
										<span v-if="field.checklist_include" class="os-field-check" title="Included in Checklist">
											<i class="fas fa-check-square"></i>
										</span>
										<span class="os-row-actions">
											<button class="os-icon-btn" title="Edit field" data-bs-toggle="modal" data-bs-target="#fieldModal" @click="editOnboardingField(field)">
												<i class="fas fa-pen"></i>
											</button>
											<button class="os-icon-btn os-icon-btn--danger" title="Delete field" @click="askRemoveField(field)">
												<i class="fas fa-trash"></i>
											</button>
										</span>
									</div>
								</draggable>
								<div v-else class="os-muted os-muted--pad">There are no fields in this step.</div>
								<button class="os-btn os-btn--sm os-add-field-btn" data-bs-toggle="modal" data-bs-target="#fieldModal" @click="setFieldOnboardingStepId(step.id)">
									<i class="fas fa-plus"></i> Add Field
								</button>
							</div>
						</div>
					</draggable>
					<div v-if="selectedType.steps.length === 0" class="os-muted os-muted--pad">
						No steps yet — add the first step to start building this onboarding type.
					</div>
				</template>
			</section>
		</div>

		<!-- PDF templates -->
		<section class="os-panel os-templates-panel">
			<div class="os-panel-head os-panel-head--top">
				<div>
					<div class="os-section-title">PDF Templates</div>
					<p class="os-hint">
						Header and footer used when PDF content is auto-generated. The placeholder <code>{customer_name}</code> is replaced with the onboarding name.
						Changes apply the next time content is prepared.
					</p>
				</div>
				<button class="os-btn os-btn--sm os-btn--primary" :disabled="savingTemplates || loadingTemplates" @click="savePdfTemplates">
					<i class="fas" :class="savingTemplates ? 'fa-spinner fa-spin' : 'fa-save'"></i> Save Templates
				</button>
			</div>
			<div v-if="loadingTemplates" class="os-muted os-muted--pad"><i class="fas fa-spinner fa-spin me-1"></i> Loading templates…</div>
			<div v-else class="os-templates-grid">
				<div>
					<label class="os-form-label">Onboarding PDF — Header</label>
					<textarea class="form-control os-template-input" rows="5" v-model="pdfTemplates.onboarding_header" />
					<label class="os-form-label mt-3">Onboarding PDF — Footer <span class="os-hint-inline">(signature lives here)</span></label>
					<textarea class="form-control os-template-input" rows="8" v-model="pdfTemplates.onboarding_footer" />
				</div>
				<div>
					<label class="os-form-label">Checklist PDF — Header</label>
					<textarea class="form-control os-template-input" rows="5" v-model="pdfTemplates.checklist_header" />
					<label class="os-form-label mt-3">Checklist PDF — Footer</label>
					<textarea class="form-control os-template-input" rows="8" v-model="pdfTemplates.checklist_footer" />
				</div>
			</div>
		</section>

		<!-- Confirm delete (type / step / field) -->
		<ConfirmModal
			ref="confirmModal"
			:title="confirm.title"
			:message="confirm.message"
			confirm-text="Delete"
			confirm-icon="fa-trash"
			:danger="true"
			:busy="confirm.busy"
			@confirm="runConfirm"
		/>

		<!-- Modal Add Onboarding Type -->
		<div class="modal fade os-modal" id="typeModal" tabindex="-1" aria-labelledby="typeModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title os-modal-title" id="typeModalLabel">Add Onboarding Type</h5>
							<p class="os-modal-sub">A type is a reusable onboarding template — its steps and fields drive every onboarding created with it.</p>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<form @submit.prevent="addOnboardingType">
							<div class="os-form-group">
								<label class="os-form-label">Name</label>
								<input class="form-control" type="text" required v-model="newOnboardingType.name" />
								<p class="os-hint">Shown in the <b>New Onboarding</b> dropdown on the dashboard — e.g. <code>New Customer</code>, <code>Upgrade Customer</code>.</p>
							</div>
							<div class="os-form-group">
								<label class="os-form-label">Description</label>
								<textarea class="form-control" v-model="newOnboardingType.description" />
								<p class="os-hint">Short internal note on when to use this type — it appears under the name in the dropdown.</p>
							</div>
						</form>
					</div>
					<div class="modal-footer">
						<button type="button" class="os-btn" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="os-btn os-btn--primary" @click="addOnboardingType()">Add Type</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Add / Update Onboarding Step -->
		<div class="modal fade os-modal" id="stepModal" tabindex="-1" aria-labelledby="stepModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-xl modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title os-modal-title" id="stepModalLabel">{{ updateStep ? 'Update Onboarding Step' : 'Add Onboarding Step' }}</h5>
							<p class="os-modal-sub">A step is one section of the onboarding call — it groups related fields (e.g. Client Details, Branded Emails).</p>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="row g-3">
							<div class="col-lg-5">
								<div class="os-form-group mb-0">
									<label class="os-form-label">Name</label>
									<input class="form-control" type="text" required v-model="stepForm.name" />
									<p class="os-hint">Shown as the section title in the onboarding sidebar — e.g. <code>Client Details</code>, <code>Theme Selection</code>.</p>
								</div>
							</div>
							<div class="col-lg-7">
								<div class="os-form-group mb-0">
									<label class="os-form-label">Description</label>
									<textarea class="form-control" rows="2" v-model="stepForm.description" />
									<p class="os-hint">Optional intro text for the step during the onboarding call.</p>
								</div>
							</div>
						</div>

						<div class="os-form-group mt-3 mb-0" v-if="stepForm.slides">
							<label class="os-form-label">Slides</label>
							<p class="os-hint mb-2">
								Links this step to slides in the onboarding presentation deck — the slide opens while the step is active. E.g. name <code>Intro</code>, slide number
								<code>4</code>. Optionally attach an image — while that slide is active, a button with the given name appears above the pagination and opens the image in a modal.
							</p>

							<div class="os-slides-table">
								<div class="os-slides-row os-slides-row--head" v-if="stepForm.slides.length">
									<div class="os-slides-head">#</div>
									<div class="os-slides-head">Name</div>
									<div class="os-slides-head">Slide No.</div>
									<div class="os-slides-head">Button name</div>
									<div class="os-slides-head">Image</div>
									<div></div>
								</div>
								<div v-for="(slide, i) in stepForm.slides" :key="i + '-slide'" class="os-slides-row">
									<div class="os-slides-index">{{ i + 1 }}</div>
									<input type="text" v-model="slide.name" class="form-control" placeholder="Slide name" />
									<input type="text" v-model="slide.number" class="form-control" placeholder="e.g. 4" />
									<input type="text" v-model="slide.button_name" class="form-control" placeholder="e.g. View themes" />
									<div class="os-slide-image">
										<a v-if="slide.image" :href="slide.image" target="_blank" rel="noopener" class="os-slide-image-thumb" title="Open image">
											<img :src="slide.image" alt="" />
										</a>
										<label class="os-btn os-btn--sm mb-0" :class="{ disabled: uploadingSlideImage === i }">
											<span v-if="uploadingSlideImage === i" class="spinner-border spinner-border-sm" role="status"></span>
											<i v-else class="fas" :class="slide.image ? 'fa-sync-alt' : 'fa-upload'"></i>
											{{ slide.image ? 'Replace' : 'Upload' }}
											<input type="file" accept="image/*" class="d-none" @change="uploadSlideImage(slide, i, $event)" />
										</label>
										<button v-if="slide.image" class="os-icon-btn os-icon-btn--danger" title="Remove image" @click="removeSlideImage(slide)">
											<i class="fas fa-times"></i>
										</button>
									</div>
									<button class="os-icon-btn os-icon-btn--danger" title="Remove slide" @click="removeStepSlide(stepForm.slides, i)">
										<i class="fas fa-trash"></i>
									</button>
								</div>
							</div>
							<button class="os-btn os-btn--sm mt-2" @click="addStepSlide(stepForm)"><i class="fas fa-plus"></i> Add Slide</button>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="os-btn" data-bs-dismiss="modal" @click="updateStep = null">Cancel</button>
						<button v-if="!updateStep" type="button" class="os-btn os-btn--primary" @click="addOnboardingStep()">Add Step</button>
						<button v-else type="button" class="os-btn os-btn--primary" @click="updateOnboardingStep()">Update</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Onboarding Field -->
		<div class="modal fade os-modal" id="fieldModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="fieldModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title os-modal-title" id="fieldModalLabel">{{ updateField ? 'Update Onboarding Field' : 'Add Onboarding Field' }}</h5>
							<p class="os-modal-sub">A field is one question or input collected during the onboarding call.</p>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="os-form-group">
							<label class="os-form-label">Name</label>
							<input class="form-control" type="text" required v-model="fieldForm.name" />
							<p class="os-hint">Label shown above the input — e.g. <code>Official Website Domain</code>.</p>
						</div>
						<div class="os-form-group">
							<label class="os-form-label">Code</label>
							<input class="form-control" type="text" required v-model="fieldForm.code" />
							<p class="os-hint">
								Optional client attribute — when it matches a column on the client record, the field is <b>prefilled automatically</b> from MC2 client data.
								E.g. <code>name</code>, <code>domain</code>, <code>county</code>. Use any unique code for manual fields.
							</p>
						</div>
						<div class="os-form-group">
							<label class="os-form-label">Type</label>
							<select class="form-select" v-model="fieldForm.type" required>
								<option value="text">Text Input</option>
								<option value="textarea">Textarea</option>
								<option value="switch">Switch Input</option>
							</select>
							<p class="os-hint"><b>Text</b> for short answers · <b>Textarea</b> for longer notes · <b>Switch</b> for Yes/No questions.</p>
						</div>
						<div class="os-form-group">
							<label class="os-form-label">Help</label>
							<input class="form-control" type="text" required v-model="fieldForm.help" />
							<p class="os-hint">Short helper text displayed under the input during onboarding — e.g. <code>Domain without https://, e.g. townofyorkwi.gov</code>.</p>
						</div>
						<div class="os-form-group">
							<label class="os-form-label">Condition</label>
							<textarea class="form-control" v-model="fieldForm.condition" />
							<p class="os-hint">
								Leave empty to always show the field. To show it <b>only when a rule matches</b>, reference another field from the <b>same step</b> by its ID
								(shown as <code>field_123</code> next to each field in the list behind this modal). Values are compared as text; a switch is
								<code>"1"</code> (on) or <code>"0"</code> (off).
							</p>
							<p class="os-hint os-hint--examples">
								Examples:<br />
								<code>field_245 == "1"</code> — show when that switch is turned on<br />
								<code>field_245 != ""</code> — show when that field has any value<br />
								<code>field_312 == "Wisconsin"</code> — show when the exact value matches
							</p>
						</div>
						<hr class="os-divider" />
						<div class="os-form-group">
							<div class="form-check form-switch">
								<input class="form-check-input" v-model="fieldForm.checklist_include" type="checkbox" id="checklistInclude" />
								<label class="form-check-label os-form-label" for="checklistInclude">Include in Checklist</label>
							</div>
							<p class="os-hint">Adds this field to the <b>Checklist PDF</b> that is generated for the client after onboarding.</p>
						</div>
						<div v-if="fieldForm.checklist_include" class="os-form-group">
							<label class="os-form-label">Checklist Help</label>
							<editor api-key="" v-model="fieldForm.checklist_help" theme="advanced" theme_advanced_toolbar_location="bottom" :init="tinyPdfToolbar" />
							<p class="os-hint">Instructions printed next to this item in the Checklist PDF — explain what the client needs to prepare or confirm.</p>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="os-btn" data-bs-dismiss="modal" @click="updateField = null">Cancel</button>
						<button v-if="!updateField" type="button" class="os-btn os-btn--primary" @click="addOnboardingField()">Add Field</button>
						<button v-else type="button" class="os-btn os-btn--primary" @click="updateOnboardingField()">Update</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../api/index'
import { Modal } from 'bootstrap'
import JuniperPageHeader from '@/components/juniper/layout/JuniperPageHeader.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import draggable from 'vuedraggable'
import editor from '@tinymce/tinymce-vue'

export default {
	name: 'OnboardingOptions',
	data() {
		return {
			loading: false,
			tinyPdfToolbar: {
				menubar: false,
				plugins: 'link code lists',
				height: 220,
				content_style: 'p { padding: 0px; margin:0px }',
				toolbar1:
					'fontselect fontsizeselect formatselect lineheight | bold italic underline strikethrough | bullist numlist | alignleft aligncenter alignright alignjustify | link removeformat code',
			},
			onboardingTypes: [],
			selectedTypeId: null,
			newOnboardingType: {
				name: '',
				description: '',
			},
			newOnboardingStep: {
				name: '',
				description: '',
				slides: [],
				onboarding_type_id: null,
				ord: 0,
			},
			newField: {
				name: '',
				code: '',
				type: 'text',
				help: '',
				condition: '',
				validation: '',
				onboarding_step_id: null,
				checklist_include: 0,
				checklist_help: '',
			},
			updateStep: null,
			updateField: null,
			uploadingSlideImage: null,
			$newOnboardingTypeModal: null,
			$onboardingStepModal: null,
			$onboardingFieldModal: null,
			expandStep: [],
			pdfTemplates: {
				onboarding_header: '',
				onboarding_footer: '',
				checklist_header: '',
				checklist_footer: '',
			},
			loadingTemplates: false,
			savingTemplates: false,
			confirm: {
				title: '',
				message: '',
				busy: false,
				action: null,
			},
		}
	},
	components: { JuniperPageHeader, ConfirmModal, draggable, editor },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		selectedType() {
			return this.onboardingTypes.find(type => type.id === this.selectedTypeId) || null
		},
		// The modals share one form template for add & edit
		stepForm() {
			return this.updateStep || this.newOnboardingStep
		},
		fieldForm() {
			return this.updateField || this.newField
		},
	},
	mounted() {
		this.$newOnboardingTypeModal = new Modal('#typeModal')
		this.$onboardingStepModal = new Modal('#stepModal')
		this.$onboardingFieldModal = new Modal('#fieldModal')
	},
	created() {
		this.getOnboartingTypes()
		this.getPdfTemplates()
	},
	methods: {
		// PDF TEMPLATES
		getPdfTemplates() {
			this.loadingTemplates = true
			restApi
				.get('/onboarding/pdf-templates')
				.then(({ data }) => {
					this.pdfTemplates = data
					this.loadingTemplates = false
				})
				.catch(error => {
					console.log(error)
					this.loadingTemplates = false
				})
		},
		savePdfTemplates() {
			this.savingTemplates = true
			restApi
				.post('/onboarding/pdf-templates', this.pdfTemplates)
				.then(({ data }) => {
					this.pdfTemplates = data
					this.savingTemplates = false
					this.$swal.fire({ icon: 'success', title: 'PDF templates saved!', timer: 2000 })
				})
				.catch(error => {
					console.log(error)
					this.savingTemplates = false
					this.$swal.fire({ icon: 'error', title: 'Saving templates failed', text: String(error) })
				})
		},
		selectType(id) {
			this.selectedTypeId = id
			this.expandStep = []
		},
		// Confirm helpers — one reusable ConfirmModal for type/step/field deletes
		askRemoveType(type) {
			this.confirm = {
				title: 'Delete onboarding type?',
				message: `"${type.name}" and its steps configuration won't be restorable.`,
				busy: false,
				action: () => this.removeOnboardingType(type.id),
			}
			this.$refs.confirmModal.open()
		},
		askRemoveStep(step) {
			this.confirm = {
				title: 'Delete step?',
				message: `"${step.name}" won't be restorable.`,
				busy: false,
				action: () => this.removeOnboardingStep(step.id),
			}
			this.$refs.confirmModal.open()
		},
		askRemoveField(field) {
			this.confirm = {
				title: 'Delete field?',
				message: `"${field.name}" won't be restorable.`,
				busy: false,
				action: () => this.removeOnboardingField(field.id),
			}
			this.$refs.confirmModal.open()
		},
		runConfirm() {
			if (this.confirm.action) {
				this.confirm.busy = true
				this.confirm.action()
			}
		},
		closeConfirm() {
			this.confirm.busy = false
			this.confirm.action = null
			this.$refs.confirmModal.close()
		},
		// TYPES
		getOnboartingTypes() {
			restApi
				.get('/onboarding-type')
				.then(({ data }) => {
					this.onboardingTypes = data
					// Keep the selection valid; default to the first type
					if (!this.onboardingTypes.find(type => type.id === this.selectedTypeId)) {
						this.selectedTypeId = this.onboardingTypes.length ? this.onboardingTypes[0].id : null
					}
				})
				.catch(error => {
					console.log(error)
				})
		},
		addOnboardingType() {
			restApi
				.post('/onboarding-type', this.newOnboardingType)
				.then(({ data }) => {
					this.getOnboartingTypes()
					this.$newOnboardingTypeModal.hide()
					this.newOnboardingType = { name: '', description: '' }
					if (data && data.id) this.selectedTypeId = data.id
				})
				.catch(error => {
					console.log(error)
				})
		},
		removeOnboardingType(id) {
			restApi
				.delete('/onboarding-type/' + id)
				.then(() => {
					this.onboardingTypes = this.onboardingTypes.filter(type => type.id !== id)
					if (this.selectedTypeId === id) {
						this.selectedTypeId = this.onboardingTypes.length ? this.onboardingTypes[0].id : null
					}
					this.closeConfirm()
				})
				.catch(error => {
					this.closeConfirm()
					this.$swal.fire({ icon: 'error', title: error })
				})
		},
		// STEPS
		addOnboardingStep() {
			restApi
				.post('/onboarding-step', this.newOnboardingStep)
				.then(() => {
					this.getOnboartingTypes()
					this.$onboardingStepModal.hide()
				})
				.catch(error => {
					console.log(error)
				})
		},
		updateOnboardingStep() {
			restApi
				.put('/onboarding-step/' + this.updateStep.id, this.updateStep)
				.then(() => {
					this.$onboardingStepModal.hide()
					this.getOnboartingTypes()
					this.updateStep = null
				})
				.catch(error => {
					console.log(error)
				})
		},
		setStepOnboardingTypeId(id, steps) {
			let count = steps.length
			this.newOnboardingStep.onboarding_type_id = id
			if (count > 0) {
				this.newOnboardingStep.ord = count - 1
			}
		},
		removeOnboardingStep(id) {
			restApi
				.delete('/onboarding-step/' + id)
				.then(() => {
					this.getOnboartingTypes()
					this.closeConfirm()
				})
				.catch(error => {
					this.closeConfirm()
					this.$swal.fire({ icon: 'error', title: error })
				})
		},
		// FIELDS
		addOnboardingField() {
			restApi
				.post('/onboarding-field', this.newField)
				.then(() => {
					this.getOnboartingTypes()
					this.$onboardingFieldModal.hide()
					this.cleanNewField()
				})
				.catch(error => {
					console.log(error)
				})
		},
		setFieldOnboardingStepId(id) {
			this.newField.onboarding_step_id = id
		},
		cleanNewField() {
			this.newField.name = ''
			this.newField.code = ''
			this.newField.help = ''
			this.newField.condition = ''
			this.newField.validation = ''
			this.newField.type = 'text'
		},
		editOnboardingField(field) {
			this.updateField = field
		},
		editOnboardingStep(step) {
			this.updateStep = step
			if (!step.slides) {
				this.updateStep.slides = []
			} else if (typeof this.updateStep.slides === 'string') {
				this.updateStep.slides = JSON.parse(this.updateStep.slides)
			}
		},
		addStepSlide(step) {
			step.slides.push({ name: '', number: '', code: '', type: '' })
		},
		removeStepSlide(slides, index) {
			slides.splice(index, 1)
		},
		uploadSlideImage(slide, index, event) {
			const file = event.target.files[0]
			event.target.value = ''
			if (!file) return

			const formData = new FormData()
			formData.append('image', file)
			this.uploadingSlideImage = index
			restApi
				.post('/onboarding-steps/slide-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
				.then(({ data }) => {
					// $set — slides added before this feature don't have image keys yet
					this.$set(slide, 'image', data.url)
					this.$set(slide, 'image_path', data.path)
					if (!slide.button_name) {
						this.$set(slide, 'button_name', slide.name || file.name.replace(/\.[^.]+$/, ''))
					}
				})
				.catch(error => {
					const errors = error.response?.data?.errors
					this.$swal.fire({ icon: 'error', title: errors ? Object.values(errors).flat().join(' ') : 'Image upload failed' })
				})
				.finally(() => {
					this.uploadingSlideImage = null
				})
		},
		removeSlideImage(slide) {
			this.$set(slide, 'image', null)
			this.$set(slide, 'image_path', null)
		},
		updateOnboardingField() {
			restApi
				.put('/onboarding-field/' + this.updateField.id, this.updateField)
				.then(() => {
					this.$onboardingFieldModal.hide()
					this.getOnboartingTypes()
					this.updateField = null
				})
				.catch(error => {
					console.log(error)
				})
		},
		removeOnboardingField(id) {
			restApi
				.delete('/onboarding-field/' + id)
				.then(() => {
					this.getOnboartingTypes()
					this.closeConfirm()
				})
				.catch(error => {
					this.closeConfirm()
					this.$swal.fire({ icon: 'error', title: error })
				})
		},
		toggleStep(stepId) {
			const index = this.expandStep.indexOf(stepId)
			if (index === -1) {
				this.expandStep.push(stepId)
			} else {
				this.expandStep.splice(index, 1)
			}
		},
		// Persist ord after drag & drop
		updateStepsOrder(steps) {
			restApi.post('/onboarding-steps/order', { steps }).catch(error => {
				console.log(error)
			})
		},
		updateFieldsOrder(fields) {
			restApi.post('/onboarding-fields/order', { fields }).catch(error => {
				console.log(error)
			})
		},
	},
}
</script>

<style scoped>
/* Master-detail layout */
.os-layout {
	display: grid;
	grid-template-columns: 280px 1fr;
	gap: 18px;
	align-items: start;
}

@media (max-width: 900px) {
	.os-layout {
		grid-template-columns: 1fr;
	}
}

.os-panel {
	background: #fff;
	border-radius: 12px;
	padding: 18px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.os-panel-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
}

.os-section-title {
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
}

/* Types master list */
.os-type-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.os-type-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	width: 100%;
	padding: 9px 12px;
	border: none;
	border-radius: 8px;
	background: transparent;
	font-size: 13.5px;
	font-weight: 500;
	color: #475569;
	text-align: left;
	cursor: pointer;
	transition: background 0.12s ease, color 0.12s ease;
}

.os-type-item:hover {
	background: #f3f5f9;
	color: #0f172a;
}

.os-type-item.active {
	background: #f8f8f8;
	color: #1565c0;
	font-weight: 600;
}

.os-type-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.os-type-count {
	background-color: #e2e8f0;
	color: #475569;
	padding: 1px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 600;
	flex-shrink: 0;
}

.os-type-item.active .os-type-count {
	background-color: #dbeafe;
	color: #1565c0;
}

/* Detail — selected type */
.os-detail-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 16px;
}

.os-detail-title {
	font-size: 17px;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.os-detail-desc {
	font-size: 13px;
	color: #6b7280;
	margin: 4px 0 0;
}

.os-detail-actions {
	display: flex;
	gap: 8px;
	flex-shrink: 0;
}

/* Steps */
.os-step {
	border: 1px solid #eef0f4;
	border-radius: 10px;
	margin-bottom: 8px;
	background: #fff;
}

.os-step-head {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	cursor: pointer;
	user-select: none;
}

.os-step-head:hover {
	background: #fafbfc;
	border-radius: 10px;
}

.os-step-chevron {
	font-size: 10px;
	color: #9ca3af;
	transition: transform 0.15s ease;
	flex-shrink: 0;
}

.os-step-chevron--open {
	transform: rotate(90deg);
}

.os-step-name {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.os-step-count {
	font-size: 11px;
	font-weight: 600;
	color: #6b7280;
	background: #f3f4f6;
	padding: 1px 8px;
	border-radius: 12px;
	flex-shrink: 0;
}

.os-row-actions {
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
}

.os-step-body {
	padding: 4px 12px 12px 34px;
	border-top: 1px solid #f6f7f9;
}

/* Fields */
.os-field {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 7px 8px;
	border-bottom: 1px solid #f1f3f5;
	font-size: 13px;
	background: #fff;
}

.os-field:last-child {
	border-bottom: none;
}

.os-field-name {
	font-weight: 500;
	color: #111827;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.os-field-code {
	font-size: 11px;
	color: #8a909c;
	background: #f6f7f9;
	padding: 1px 6px;
	border-radius: 5px;
	flex-shrink: 0;
}

.os-field-check {
	color: #16a34a;
	font-size: 12px;
	flex-shrink: 0;
}

.os-add-field-btn {
	margin-top: 10px;
}

/* Drag handle */
.os-drag {
	color: #d1d5db;
	cursor: grab;
	font-size: 11px;
	flex-shrink: 0;
	padding: 2px;
}

.os-drag:hover {
	color: #9ca3af;
}

.sortable-ghost {
	opacity: 0.4;
}

/* Flat buttons — cd-btn design */
.os-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 9px;
	font-size: 0.85rem;
	font-weight: 600;
	background: transparent;
	color: #2b303b;
	cursor: pointer;
	transition: all 0.12s ease;
}

.os-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.os-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

.os-btn--danger {
	color: #dc2626;
	border-color: #fecaca;
}

.os-btn--danger:hover:not(:disabled) {
	background: #fee2e2;
	border-color: #fca5a5;
	color: #dc2626;
}

.os-btn--primary {
	color: #2b6cff;
	border-color: #b6cfff;
}

.os-btn--primary:hover:not(:disabled) {
	background: #ebf2ff;
	border-color: #8fb4ff;
	color: #2b6cff;
}

.os-btn i {
	font-size: 0.72em;
}

/* Icon-only row actions — plain gray, colour on hover */
.os-icon-btn {
	background: none;
	border: none;
	padding: 5px 7px;
	border-radius: 5px;
	color: #adb5bd;
	font-size: 12px;
	cursor: pointer;
	transition: color 0.12s ease, background 0.12s ease;
}

.os-icon-btn:hover {
	color: #475569;
	background: #f3f5f9;
}

.os-icon-btn--danger:hover {
	color: #dc2626;
	background: #fee2e2;
}

/* Misc */
.os-muted {
	font-size: 13px;
	color: #8a909c;
}

.os-muted--pad {
	padding: 12px 4px;
}

.os-empty {
	min-height: 220px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	color: #8a909c;
	font-size: 13px;
	text-align: center;
}

.os-empty-icon {
	font-size: 34px;
	color: #d1d5db;
}

/* Modals — flat Juniper look (ConfirmModal design language) */
.os-modal .modal-content {
	border: 1px solid #e6e8ec;
	border-radius: 14px;
}

.os-modal .modal-header {
	border-bottom: none;
	padding-bottom: 4px;
	align-items: flex-start;
}

.os-modal .modal-footer {
	border-top: none;
	padding-top: 4px;
	gap: 4px;
}

.os-modal-title {
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.os-modal-sub {
	font-size: 12px;
	color: #8a909c;
	margin: 3px 0 0;
	line-height: 1.4;
}

.os-form-group {
	margin-bottom: 14px;
}

.os-form-label {
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 4px;
}

.os-hint {
	font-size: 12px;
	color: #8a909c;
	line-height: 1.45;
	margin: 5px 0 0;
}

.os-hint code {
	font-size: 11px;
	color: #4a5260;
	background: #f6f7f9;
	padding: 1px 5px;
	border-radius: 5px;
}

.os-hint--examples {
	background: #f8f9fb;
	border: 1px solid #eef0f4;
	border-radius: 8px;
	padding: 8px 10px;
	margin-top: 6px;
	line-height: 1.8;
}

.os-divider {
	border-color: #eef0f4;
	opacity: 1;
	margin: 16px 0;
}

.os-slides-head {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
}

.os-slides-table {
	border: 1px solid #eef0f4;
	border-radius: 10px;
	overflow: hidden;
}

.os-slides-row {
	display: grid;
	grid-template-columns: 28px minmax(0, 1.3fr) 110px minmax(0, 1.3fr) 230px 32px;
	gap: 10px;
	align-items: center;
	padding: 8px 12px;
	border-top: 1px solid #f1f3f5;
}

.os-slides-row:first-child {
	border-top: 0;
}

.os-slides-row--head {
	padding-top: 10px;
	padding-bottom: 6px;
	background: #fafbfc;
}

.os-slides-row .form-control {
	height: 36px;
	font-size: 14px;
}

.os-slides-index {
	font-size: 12px;
	font-weight: 600;
	color: #9ca3af;
	text-align: center;
}

.os-slide-image {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.os-slide-image-thumb {
	display: block;
	width: 52px;
	height: 36px;
	flex-shrink: 0;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	overflow: hidden;
}

.os-slide-image-thumb img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.os-slide-image label.os-btn {
	cursor: pointer;
	white-space: nowrap;
}

/* Narrow screens: name/number on one line, image controls below */
@media (max-width: 991px) {
	.os-slides-row {
		grid-template-columns: 28px minmax(0, 1fr) 90px 32px;
	}

	.os-slides-row--head {
		display: none;
	}

	.os-slides-row > :nth-child(4) {
		grid-column: 2 / 4;
		grid-row: 2;
	}

	.os-slides-row > :nth-child(5) {
		grid-column: 2 / 4;
		grid-row: 3;
	}

	.os-slides-row > :nth-child(6) {
		grid-column: 4;
		grid-row: 1;
	}
}

/* design.scss forces a 360px min-height on all TinyMCE instances — the
   checklist help editor should stay compact. */
.os-modal ::v-deep .tox.tox-tinymce {
	min-height: 220px;
}

/* PDF templates panel */
.os-templates-panel {
	margin-top: 18px;
}

.os-panel-head--top {
	align-items: flex-start;
}

.os-panel-head--top .os-hint {
	max-width: 640px;
}

.os-templates-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 18px;
}

@media (max-width: 900px) {
	.os-templates-grid {
		grid-template-columns: 1fr;
	}
}

.os-template-input {
	font-family: SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 12px;
	line-height: 1.5;
	color: #374151;
	margin-top: 4px;
}

.os-hint-inline {
	font-size: 11px;
	font-weight: 500;
	color: #9ca3af;
}
</style>
